import { gql, useQuery } from '@apollo/client'

import type { CartInfoInput } from '../types'
import type { RemoteCartInfo } from '@@types'

const CART_INFO_QUERY = gql`
  query CartInfo(
    $cartInfo: CartInfoInput!
    $style: String
    $imageStyle2: String
    $imagesStyle2: String
    $previewStyle2: String
  ) {
    cartInfo(cartInfo: $cartInfo) {
      totalWeight
      promocode
      promocodeType
      priceGroup
      totals {
        count
        baseTotal
        total
        baseSale
        sale
        salePercent
        maxSale
        promoPercent
        effectiveTotal
      }
      delivery {
        total
        sale
        salePercent
        maxSale
        promoPercent
        type
        from
        timeRestrictions {
          from
          to
        }
      }
      products {
        id
        amount
        shop_id
        total
        comment
        info {
          id
          name
          price
          oldPrice
          price_type
          isWeightProduct
          count_multiplier
          isFavorite
          stock {
            amount
          }
          categories {
            id
            name
            parent_id
          }
          preview(style: $previewStyle2) {
            url
          }
          slug
          review_count
          marketingLabels {
            id
            title
            description
            image(style: "600xauto") {
              url
            }
            activeFrom
            activeUntil
            optionColor
            optionBackgroundColor
            optionLink
            optionLinkLabel
          }
          is_alcohol
          priceOffers {
            id
            price
            oldPrice
            salePeriod {
              start
              finish
            }
            min
            max
            step
            quant
            uiQuant
            quantAmount
            isDefault
            group
          }
          priceOffer @client {
            id
            price
            oldPrice
            count_multiplier
            salePeriod {
              start
              finish
            }
            min
            max
            step
            quant
            quantAmount
            isDefault
            group
          }
        }
      }
      sales {
        salePluginId
        bonusCurrencyId
        amount
        sale
        salePercent
        type
      }
      saleOffers {
        id
        title
        reason
        reasonId
        type
        discount
        maxDiscount
        discountCurrencyId
        salePluginId
      }
      saleErrors {
        internalCode
        internalIssuer
        type
        message
        inputPart {
          spendBonuses {
            bonusCurrencyId
            amount
          }
          coupons {
            barcode
          }
          applySales {
            saleId
          }
          promocodes {
            code
          }
        }
      }
    }
  }
`

export { CART_INFO_QUERY }

export interface CartInfoVariables {
  cartInfo: CartInfoInput
  style?: string
  imageStyle2?: string
  imagesStyle2?: string
  previewStyle2?: string
}

export interface CartInfoResponse {
  cartInfo: RemoteCartInfo | null
}

/**
 * Hook to fetch cart info with automatic updates when variables change
 * Uses Apollo cache for data management
 */
export function useCartInfo (variables: CartInfoVariables, skip = false) {
  const {
    data,
    loading,
    error,
    refetch,
    previousData,
  } = useQuery<CartInfoResponse, CartInfoVariables>(
    CART_INFO_QUERY,
    {
      variables,
      skip,
      // Use cache-first to avoid flicker, then revalidate in background
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      returnPartialData: true,
    },
  )

  return {
    cartInfo: data?.cartInfo ?? previousData?.cartInfo ?? null,
    loading,
    error,
    refetch,
  }
}
